import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { SPACING, RADIUS, COLORS, TYPOGRAPHY } from '../designSystem';

/**
 * Smart back navigation hook that:
 * 1. Uses browser history (navigate(-1)) when appropriate
 * 2. Falls back to a logical default route if history is unavailable
 *
 * @param {string} pageType - 'merchant-detail', 'deal-detail', 'merchant-edit', 'deal-edit', 'list'
 * @param {string} fallbackRoute - Default route to go to if history can't be used
 * @returns {Function} Handler to call on back button click
 */
export function useBackNavigation(pageType, fallbackRoute) {
  const navigate = useNavigate();

  return useCallback(() => {
    // Strategy: Try to use browser history first (navigate(-1))
    // This ensures we go back to wherever the user came from naturally
    // However, we need to be careful not to go outside the app

    // Check if there's meaningful browser history
    // window.history.length tells us how many entries are in the history stack
    // If length > 1, we have a previous page in the history
    if (window.history.length > 1) {
      // Use browser history to go back
      // This respects the actual navigation path the user took
      navigate(-1);
    } else {
      // No history available (e.g., direct page load, back button exhausted)
      // Use the sensible fallback for this page type
      navigate(fallbackRoute);
    }
  }, [navigate, fallbackRoute]);
}

/**
 * Helper to create a back button element for the header
 * Uses the same filled primary button style as other header action buttons
 * @param {Function} onBack - The back handler from useBackNavigation
 * @param {Object} customStyles - Optional style overrides
 * @returns {JSX.Element}
 */
export function createBackButton(onBack, customStyles = {}) {
  return (
    <button
      onClick={onBack}
      style={{
        padding: `${SPACING.MD} ${SPACING.XL}`,
        backgroundColor: COLORS.PRIMARY_DARK,
        color: COLORS.TEXT_WHITE,
        border: 'none',
        borderRadius: RADIUS.DEFAULT,
        fontSize: TYPOGRAPHY.BODY.fontSize,
        fontWeight: '700',
        fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
        cursor: 'pointer',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: SPACING.SM,
        boxShadow: '0 4px 12px rgba(77, 219, 148, 0.3)',
        letterSpacing: '0.3px',
        ...customStyles,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(77, 219, 148, 0.45)';
        e.currentTarget.style.backgroundColor = '#0a2c25';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(77, 219, 148, 0.3)';
        e.currentTarget.style.backgroundColor = COLORS.PRIMARY_DARK;
      }}
    >
      ← Back
    </button>
  );
}
